// Convenience consts for ms time-based (setTimeout, etc.) usages
export const SECONDS = 1000;
export const MINUTES = 60 * SECONDS;
export const HOURS = 60 * MINUTES;
export const DAYS = 24 * HOURS;

const constants = {
    APP_NAME: "Geotab Drive",
    DEFAULT_SERVER: "my.geotab.com",
    HOS: {
        RULE_CALCULATION_INTERVAL: 1 * MINUTES,
        STATUS_CHANGE_DELAY: 1 * SECONDS,
        CHECK_ON_LOG_INTERVAL: 5 * MINUTES,
        IOX_LOG_LENIENCY: 20 * SECONDS,
        TOAST_MESSAGE_TIMEOUT: 6 * SECONDS,
        DUTY_STATUS_TOAST_TIMEOUT: 12.5 * SECONDS,
        TOAST_MESSAGE_DELAY_MAX: 10 * SECONDS,
        DISABLE_STATUS_TOAST: false // For testing!
    },
    DVIR: {
        GEOLOCATION_API_TIMEOUT: 10 * SECONDS
    },
    ON_DEMAND_INTERVAL: SECONDS,
    SYNC: 15 * MINUTES,
    SYNC_HOS_INTERVAL: 5 * MINUTES,
    DTC_MONITOR: 24 * HOURS,
    DTC_MONITOR_NO_VEHICLE_RETRY: 15 * MINUTES,

    // Threshold log count per store before compression is triggered
    ALLOWED_UNCOMPRESSED_HOSLOGS_AMOUNT: 3000,
    SYSTEM_HOS_LOGS_MAX_SAVED_COUNT: 5000,

    // Minimum total log count before pruning is attempted
    MIN_LOGS_FOR_PRUNING: 3000,
    SYNC_DEFECTLISTS: 12 * HOURS,
    SYNC_EVENTS: 30 * MINUTES,
    RAPID_SYNC_EVENTS: 5 * MINUTES,
    SYSTEM_SETTINGS_INTERVAL: 1 * HOURS,
    DRIVER_CHANGE_INTERVAL: 10 * MINUTES,
    CLOCK_DRIFT_INTERVAL: 10 * MINUTES,
    SHOW_FIVE_MIN_STOPPED_DIALOG_INTERVAL: 5 * MINUTES,
    SHOW_FIVE_MIN_STOPPED_RETRY_INTERVAL: 1 * MINUTES,
    APP_VERSION: 0.69,
    SERVICE_WORKER_UPDATE_RETRY_TIMEOUT: 20 * MINUTES,
    QUOTA_EXCEEDED_ERROR_CODE: 22,
    QUOTA_EXCEEDED_ERROR: "QuotaExceededError",
    MIN_SERVER_VERSION: "5.7.1.40091",
    DEVICE_COMMUNICATING_GRACE_PERIOD_MS: 15 * MINUTES,
    OFFLINE_MESSAGE_GRACE_PERIOD_MS: 5 * MINUTES,
    NETWORK_TOAST_MESSAGE_GRACE_PERIOD_MS: 10 * SECONDS,
    CHECK_NETWORK_GRACE_PERIOD_MS: 10 * SECONDS,
    FILE_READ_TIMEMOUT: 5 * 1000,
    MEDIA_SOLUTION_ID: "adDcLOG8Q9UaRs3eSgGunTA",
    SUPPORTED_MEDIA_TYPES: {
        IMAGE: "Image"
    },
    DRVFS_ROOT: "drvfs:///",
    MEDIA_FILE_RETENTION_DAYS: 4,
    BRIDGE: {
        REFRESH_INTERVAL: 15 * MINUTES
    },
    LOGGING: {
        LENGTH: 1000,
        BQ_MAX_LENGTH: 2000,
        LOG_SIZE_LIMIT: 1000, // Characters recorded for a single log message
        DUMP_INTERVAL: 10 * SECONDS,
        VERBOSE_OVERRIDE: false,
        DEFAULT_UPLOAD_CHUNK_SIZE: 100,
        SCHEDULED_TASK_UPLOAD_CHUNK_SIZE: 50,
        DEFAULT_UPLOAD_TIMEOUT: 1 * MINUTES,
        DEFAULT_MAX_UPLOAD_LOOPS: 10, // Default maximum number of upload iterations per invocation of uploadEventLogsFromIndexedDb
        MAX_LOGS_PER_UPLOAD_CYCLE: 5000 // Hard ceiling on TraceText calls per cycle (~1,000/min at 5-min intervals). Bounds runaway queues so Infra can tighten the server-side limit.
    },
    SESSION: {
        MAX_LOGGED_IN_USERS: 3,              // The maximum count of all users, including the current driver and co-drivers
        MAX_LOGGED_IN_USERS_OVERRIDE: 4,     // Override for MAX_LOGGED_IN_USERS, used when FF MAX_DRIVERS_LIMIT_INCREASE is enabled
        DEFAULT_EXPIRY: 14 * DAYS,           // Default expiry for a session from the server-side, not changable here. See SessionManager.defaultSessionTimeoutPeriod for up-to-date value.
        DEFAULT_LONG_EXPIRY: 365 * DAYS,     // Default expiry for a long session from the server-side, not changable here. See SessionManager.defaultLongSessionTimeoutPeriod for up-to-date value.
        AUTO_LOGOUT_BEFORE_EXPIRY: 1 * DAYS, // Time before the default expiry that the app should attempt a logout,
        CHECK_EXPIRY_INTERVAL: 10 * MINUTES  // Interval to check if it's an optimal time for a session expiry
    },
    DRIVING: {
        TRACKING: 1 * MINUTES
    },
    DEVICE_ASSIGN: {
        CHECK_INTERVAL: 5 * MINUTES,
        MIN_RADIUS: 10, // metres
        MAX_RADIUS: 500, // metres
        MOVING_THRESHOLD_SPEED: 1, // km/h
        MOVING_THRESHOLD_ANGLE: 60, // degrees
        MOVING_THRESHOLD_MAX_SERVER_DELAY_SECS: 40,
        STATIONARY_ASSIGNMENT_ENABLED: false /// true, if we can assign vehicles when the tablet/phone is not moving or slow moving
    },
    MESSAGING: {
        MESSAGE_EXPIRY_DAYS: 7,
        ROUTE_GROUPING_GRACE_INTERVAL_SECS: 10
    },
    LOGOUT_ADDIN_EXECUTION_TIMELIMIT: 15 * SECONDS,
    DEVICE_UNASSIGN: {
        RADIUS: 0.5, // km
        MIN_PERIOD_AFTER_ON_LOG: 2 * MINUTES,
        MAX_PERIOD_AFTER_ON_LOG: 1 * HOURS,
        MIN_ACCURACY: 20 // metres
    },
    CAMELCASE_SEPARATOR_REGEXP: /(?=[A-Z][^A-Z])/,
    SPACE_SEPARATOR: " ",
    MIN_DATE: new Date(Date.UTC(1986, 0, 1)),
    MAX_DATE: new Date(Date.UTC(2050, 0, 1)),
    CANADA_MAX_PC_LIMIT: 75000, // meters
    ASSET_LIMITS: {
        MAX_TRAILERS_LIMIT: 7,
        MAX_SHIPMENTS_LIMIT: 25
    },
    NEWS_CHECK_INTERVAL: 2 * HOURS,
    CACHE: {
        REFRESH_INTERVAL: 1 * HOURS
    },
    CLEAR_ADDRESS_CACHE: 30 * MINUTES,
    SET_RULESETS_INTERVAL: 24 * HOURS,
    DIALOG: {
        SELF_CLOSING_TIMEOUT_SECS: 60
    },
    CANADA_RECIPIENT_EMAIL_ADDRESS: "eldtransfertesting@geotab.com",
    LOCKSCREEN_PROXIMITY_RADIUS: 25, // km
    NOOP: () => { /* Empty function */ },

    FIVE_MPH: 8.04672,
    FEEDBACK: {
        EXTERNAL_SUPPORT_URL: "",
        SUPPORTED_LANGUAGES: [] as string[]
    },
    CHECK_SCREEN_LOCK_INTERVAL: 5 * SECONDS,
    FEATURE_FLAG_INTERVAL: 5 * MINUTES,
    IS_FEATURE_FLAG_ENABLED_TIMEOUT: 10 * SECONDS,
    MOVEMENT: {
        MAX_ACCURACY: 1000,
        GET_AVERAGE_ACCURACY_FACTOR: (averageAccuracy: number) => {
            if (averageAccuracy <= 10) {
                return 5; // 5x
            } else if (averageAccuracy > 10 && averageAccuracy <= 100) {
                return 2; // 2x
            } else {
                return 1.5; // 1.5x
            }
        },
        AVERAGE_ACCURACY_MINIMUM_SAMPLES: 50, // minimum count of accuracy records needed to calculate an average
        AVERAGE_ACCURACY_MAXIMUM_SAMPLES: 200, // maximum count of accuracy records to retain for calculating an average
        DEFAULT_DRIVING_THRESHOLD: 8, // km/h
        STOPPING_THRESHOLD: 4, // km/h
        SILENCE_DURATION: 10 * SECONDS,
        DEFAULT_DRIVE_TIMEOUT_DELAY: 7 * SECONDS,
        STOP_TRACKING_WAIT_INTERVAL: 60 * SECONDS,
        ENSURE_WATCH_POSITION_TIME: 60 * SECONDS,
        GPS_VALID_TIME_LIMIT: 5 * SECONDS
    },
    IAM: {
        GET_DISCOVERY_URI: (loginUrl: string) => `${ loginUrl }/.well-known/openid-configuration`,
        OPTIMISTIC_REFRESH_WINDOW: 10 * MINUTES, // 10 minutes
        OPTIMISTIC_API_TIMEOUT: 1 * SECONDS, // 1 seconds
        OPTIMISTIC_API_MAX_DURATION: 1 * SECONDS, // 1 seconds
        REFRESH_DEBOUNCE_TIME: 10 * SECONDS,
        BACKOFF_TIME_VALUES: [21, 34, 55, 89, 144, 233],
        RETRIES_BEFORE_DISABLING_NETWORK_ERROR_LOGGING: 3,
        MINIMUM_CHROME_VERSION: 70,
        MINIMUM_SAFARI_VERSION: 11,
        SILENT_REFRESH_TIMEOUT_SECONDS: 60 * 4, // 4 minutes in seconds
        USER_SERVICE_ACCOUNT_AUTHENTICATION_TYPE: "ServiceAccount",
        IAM_ADDIN_MANAGER_SESSION_VALIDATION_DEBOUNCE: 1 * MINUTES,
        INCLUDED_OIDC_CLIENT_METHODS_FOR_LOGGING: [
            "signinSilent",
            "_loadUser",
            "WebStorageStateStore",
            "useRefreshToken",
            "exchangeRefreshToken",
            "validateRefreshResponse",
            "storeUser",
            "Event('User loaded')"
        ],
        ERROR: [
            "Maximum allowed refresh token reuse exceeded",
            "Offline user session not found",
            "Invalid refresh token. Token client and authorized client don't match",
            "Stale token",
            "Token is not active",
            "Offline session not active",
            "Session not active",
            "Invalid refresh token",
            "Refresh token issued before the user session started",
            "refresh token issued before the client session started",
            "Session doesn't have required client",
            "Client session not active",
            "Unmatching clients",
            "Client no longer has requested consent from user",
            "Client certificate missing, or its thumbprint and one in the refresh token did NOT match",
            "Token verification failure",
            "No DPoP confirmation in access token",
            "No DPoP key thumbprint in access token",
            "DPoP confirmation doesn't match DPoP proof",
            "login_required",
            "User is not an IAM user."
        ],
        PROTECTED_PAGES: [
            "ui/login",
            "ui/workflow",
            "ui/inspection"
        ]
    },
    GROUPS: {
        ENTIRE_ORGANIZATION_GROUP_ID: "GroupCompanyId",
        ASSET_GROUP_ID: "GroupAssetTypeId",
        VEHICLE_GROUP_ID: "GroupVehicleId",
        TRAILER_GROUP_ID: "GroupTrailerId",
        EQUIPMENT_GROUP_ID: "GroupEquipmentId",
        CONTAINER_GROUP_ID: "GroupContainerId"
    },
    ZENITH_ROUTE: "/-/",
    COLD_CHAIN_ROUTE: "coldChain",
    TIME_BETWEEN_REFRESH_CURRENT_USER: 30 * MINUTES,
    WEBSOCKETS: {
        /** How often the @wsPing scheduler task sends a drive-ping over the active channel to probe liveness. */
        PING_INTERVAL_MS: 1 * MINUTES,
        /**
         * Maximum time (ms) to wait for a drive-pong response after sending drive-ping.
         * Matches socket.io Engine.IO v4 default pingTimeout.
         * If no pong arrives within this window the consecutive-miss counter is incremented;
         * two consecutive misses trigger a reconnect.
         */
        PING_TIMEOUT_MS: 10 * SECONDS,
        /**
         * Upper bound (ms) for the random delay applied before triggering a WebSocket reconnect.
         * Spreads simultaneous reconnect attempts across clients after a server restart so the
         * server is not flooded by a thundering herd when it comes back online.
         */
        RECONNECT_JITTER_MAX_MS: 30 * SECONDS
    }
};

if (window.whitelabel) {
    switch (window.whitelabel) {
        case "spireon":
            constants.APP_NAME = "FleetLocate";
            break;
        case "transflo":
            constants.APP_NAME = "Transflo";
            break;
        case "smartdrive":
            constants.APP_NAME = "SmartDrive Compliance";
            break;
        case "eleos":
            constants.APP_NAME = "Eleos Drive ELD";
            break;
        case "vodafone":
            constants.APP_NAME = "Vodafone Fleet Drive";
            break;
        case "shell":
            constants.APP_NAME = "Shell Telematics (Driver)";
            break;
        case "lytx":
            constants.APP_NAME = "Lytx ELD";
            break;
        case "kia":
            constants.APP_NAME = "Kia Pleos";
            constants.FEEDBACK.EXTERNAL_SUPPORT_URL = "https://cloud.eu.connect.kia.com/AppWebform_KiaPleosFleetDriverApp?lang={LANGUAGE}";
            constants.FEEDBACK.SUPPORTED_LANGUAGES =  ["de", "cs", "da", "fi", "fr", "hu", "en", "it", "nl", "no", "pl", "sk", "es", "sv"];
            break;
        case "ford":
            constants.APP_NAME = "Ford Pro Telematics ELD";
            break;
        default:
            break;
    }
}

export default constants;